import type { ContractCoverage } from '../../api/schema'

/**
 * Coverage of a commitment by assembly resolutions (spec 2026-10-05-02).
 * Pure helpers: the API answers in cents, the screen shows "240 k€".
 */

type Authorization = NonNullable<ContractCoverage['authorizations']>[number]

/** A money amount in cents, compact for big figures: 24000000 cents -> "240 k€". */
export function formatCents(cents: number, currency: string | undefined, locale: string): string {
    const money = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'EUR',
        maximumFractionDigits: 0,
        notation: Math.abs(cents) >= 10_000_000 ? 'compact' : 'standard',
    })
    return money.format(cents / 100)
}

export type CoverageState = 'covered' | 'pending' | 'none'

/**
 * covered: an authorization is effective today. pending: an authorization is
 * linked but none is effective yet (its reason says why). none: nothing links
 * the commitment to a resolution.
 */
export function coverageState(cov: Pick<ContractCoverage, 'authorized' | 'covered'>): CoverageState {
    if (cov.covered) return 'covered'
    return cov.authorized ? 'pending' : 'none'
}

/** The authorization to name: the first effective one, else the first linked. */
export function mainAuthorization(cov: Pick<ContractCoverage, 'authorizations'>): Authorization | undefined {
    const list = cov.authorizations ?? []
    return list.find((a) => a.effective) ?? list[0]
}

export function authorizationDate(a: Authorization | undefined, locale: string): string {
    if (!a?.held_at) return ''
    return new Date(a.held_at).toLocaleDateString(locale, { day: 'numeric', month: 'long' })
}
